import { useState } from 'react';
import { Button, Icon, Modal } from 'semantic-ui-react';
import StatusMessage from 'components/modal/StatusMessage';
import GenericButton, { GenericButtonTypes } from 'components/buttons/GenericButton';
import type { FunctionalComponent, GenericModalProps } from 'apps/core/types/global.types';
import styled from 'styled-components';

const StyledModalActions = styled(Modal.Actions)`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$statusMessage ? 'space-between' : 'flex-end')};
  ${(props) => props.$statusMessage && 'span:first-child { text-align: left; }'}
  @media only screen and (max-width: 767px) {
    margin-bottom: 1rem;
  }
`;

const StyledDiv = styled.div`
  display: inline;
  min-width: 172px;
`;

const GenericModal: FunctionalComponent<GenericModalProps> = ({
  title,
  size,
  isSubmitDisabled,
  isDeleteDisabled,
  isLoading,
  isDeleting,
  handleSubmitClick,
  children,
  description,
  statusMessage,
  submitButtonText = 'Save',
  cancelButtonText,
  onClose,
  helpSection,
  showDeleteButton,
  onDelete,
  closeOnDimmerClick,
  imageContent,
  isCancelDisabled,
  dataTestId,
  cancelButtonType,
}) => {
  const [isHelpSectionOpen, setIsHelpSectionOpen] = useState(false);

  return (
    <Modal
      closeOnEscape
      closeOnDimmerClick={closeOnDimmerClick}
      onClose={() => onClose()}
      open
      size={size ?? 'large'}
      data-testid={dataTestId}
    >
      {title && <Modal.Header>{title}</Modal.Header>}
      <Modal.Content image={imageContent}>
        {children}
        {description && <Modal.Description>{description}</Modal.Description>}
        {isHelpSectionOpen && helpSection}
      </Modal.Content>
      <StyledModalActions $statusMessage={statusMessage}>
        {statusMessage && <StatusMessage message={statusMessage.message} type={statusMessage.type} />}
        <StyledDiv>
          {helpSection && (
            <Button icon basic onClick={() => setIsHelpSectionOpen(!isHelpSectionOpen)}>
              <Icon name="question circle outline" />
            </Button>
          )}
          {showDeleteButton && (
            <GenericButton
              type={GenericButtonTypes.DELETE}
              icon={false}
              onClick={() => onDelete?.()}
              disabled={isLoading || isDeleteDisabled}
              loading={isDeleting}
            />
          )}
          <GenericButton
            disabled={isCancelDisabled}
            withPrefix={!cancelButtonText}
            type={cancelButtonType ?? GenericButtonTypes.CANCEL}
            icon={false}
            content={cancelButtonText}
            onClick={() => onClose()}
          />
          {handleSubmitClick && (
            <GenericButton
              type={GenericButtonTypes.SAVE}
              icon={false}
              disabled={isSubmitDisabled || isDeleting}
              withPrefix={false}
              content={submitButtonText}
              loading={isLoading}
              onClick={handleSubmitClick}
            />
          )}
        </StyledDiv>
      </StyledModalActions>
    </Modal>
  );
};

export default GenericModal;
